import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { PreparedStarterInfo, TagsCollection } from '@racemap/utilities/functions/timing';
import { isStageGroupEvent } from '@racemap/utilities/functions/utils';
import type { Immutable } from 'immer';
import { type CSSProperties, type FC, useMemo, useState } from 'react';
import { AutoSizer, MultiGrid, type ScrollbarPresenceParams } from 'react-virtualized';
import 'react-virtualized/styles.css';
import {
  useRelevantEventsData,
  useSelectedEventId,
} from 'src/store/leaderboard/leaderboard_reducer';
import { useWindowSize } from 'usehooks-ts';
import { PlayerLinkButton } from '../../BasicComponents/PlayerLinkButton/PlayerLinkButton';
import { SearchBar } from '../../BasicComponents/SearchBar/SearchBar';
import { getFilteredStarters, getSortedStarters, getTagsWithCounts } from '../../utils/helpers';
import { FilterModal } from '../FilterModal';
import { SettingsModal } from '../SettingsModal';
import { UploadButton } from '../UploadButton';
import { useLeaderboardEnvironmentInfo } from '../utils';
import { appendNoneTags, getFilteredTags } from './Columns/TagCol';
import { TableCell } from './TableCell';
import { TableHeaderCell } from './TableHeaderCell';
import type { Sorting } from './TableHeaderCell';
import {
  getColumnsForAtomicEvent,
  getColumnsForStageGroupEvent,
  getFixedColumnCount,
} from './helpers';

export interface Column {
  key: string;
  title: string | React.ReactNode;
  width: number;
  render: (row: Immutable<PreparedStarterInfo>) => React.ReactNode;
  headerRender?: (column: Column, sorting: Sorting | null) => React.ReactNode;
  sorter?: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) => number;
}

export const TableBody: FC = () => {
  const { isEmbedded } = useLeaderboardEnvironmentInfo();
  const selectedEventId = useSelectedEventId();
  const events = useRelevantEventsData();
  const selectedEventData = selectedEventId != null ? events.get(selectedEventId) : undefined;

  if (selectedEventData == null) return <></>;

  const { eventObject, splits, tags, starterData, timekeepings } = selectedEventData;
  const filteredTags = getFilteredTags(tags, eventObject);
  const eventDataWithNoneTags = appendNoneTags(starterData, tags);

  const [showOnline, setShowOnline] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showStartTime, setShowStartTime] = useState(false);
  const [showDeltaTime, setShowDeltaTime] = useState(false);
  const [selectedTags, setSelectedTags] = useState<TagsCollection>(new Map());
  const [scrollBarInfos, setScrollBarInfos] = useState<ScrollbarPresenceParams | null>(null);
  const [sorting, setSorting] = useState<{ key: string; order: 'ASC' | 'DESC' } | null>(null);
  const { width: windowWidth } = useWindowSize();

  const starters = eventDataWithNoneTags.starters;
  const visibleElements = eventObject.modules.timing.visibleElements;
  const displayMapLink = visibleElements.mapLink && !isEmbedded;
  const isStageGroup = isStageGroupEvent(eventObject);

  const columns: Array<Column> = [];
  if (isStageGroup) {
    columns.push(...getColumnsForStageGroupEvent(eventObject, filteredTags, events, windowWidth));
  } else {
    columns.push(
      ...getColumnsForAtomicEvent(
        eventObject,
        splits,
        eventDataWithNoneTags,
        timekeepings,
        filteredTags,
        windowWidth,
        showStartTime,
        showDeltaTime,
        isEmbedded,
      ),
    );
  }

  const filteredStarters = useMemo(
    () => getFilteredStarters<PreparedStarterInfo>(starters, searchValue, showOnline, selectedTags),
    [starters, searchValue, showOnline, selectedTags],
  );

  const tagsWithCounts = useMemo(
    () => getTagsWithCounts(starters, filteredTags, selectedTags),
    [starters, filteredStarters, selectedTags],
  );
  const sortedStarters = useMemo(
    () => getSortedStarters(filteredStarters, sorting, columns, events, selectedEventData),
    [filteredStarters, sorting, columns, events, selectedEventData],
  );

  function cellRenderer({
    columnIndex,
    key,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    key: string;
    rowIndex: number;
    style: CSSProperties;
  }) {
    const column = columns[columnIndex];
    if (rowIndex === 0) {
      return (
        <TableHeaderCell
          key={key}
          cellKey={key}
          style={style}
          column={column}
          sorting={sorting}
          setSorting={setSorting}
        />
      );
    }
    // Regular row
    const starter = sortedStarters[rowIndex - 1];

    return (
      <TableCell
        key={key}
        cellKey={key}
        style={style}
        column={column}
        starter={starter}
        rowIndex={rowIndex}
      />
    );
  }

  const totalWidth =
    columns.reduce((prev, cur) => prev + cur.width, 0) +
    (scrollBarInfos?.vertical ? scrollBarInfos.size : 0);

  return (
    <Container style={{ maxWidth: totalWidth }} noPadding={isEmbedded}>
      <ControlBar withPadding={isEmbedded}>
        <SearchBar value={searchValue} onChange={setSearchValue} />
        {displayMapLink && <PlayerLinkButton event={eventObject} />}
        {filteredTags.size > 0 && (
          <FilterModal
            tags={tagsWithCounts}
            selectedTags={selectedTags}
            onChange={setSelectedTags}
          />
        )}
        <UploadButton />
        <SettingsModal
          event={eventObject}
          showStartTime={showStartTime}
          onShowStartTimeChange={setShowStartTime}
          showDeltaTime={showDeltaTime}
          onShowDeltaTimeChange={setShowDeltaTime}
          showOnline={showOnline}
          onShowOnlineChange={setShowOnline}
        />
      </ControlBar>
      <Content>
        <AutoSizer>
          {({ height, width }) => (
            <MultiGridContainer
              noBorderRadius={isEmbedded}
              style={{
                width,
                height,
              }}
            >
              <MultiGrid
                cellRenderer={cellRenderer}
                columnCount={columns.length}
                columnWidth={({ index }) => {
                  return columns[index]?.width || 0;
                }}
                height={height}
                rowCount={sortedStarters.length + 1}
                rowHeight={({ index }) => (index === 0 ? 60 : 45)}
                width={width}
                overscanColumnCount={Math.min(columns.length, 4)}
                overscanRowCount={100}
                fixedRowCount={1}
                fixedColumnCount={getFixedColumnCount(eventObject, windowWidth)}
                enableFixedColumnScroll
                enableFixedRowScroll
                classNameTopLeftGrid="multigrid-top multigrid-left"
                classNameTopRightGrid="multigrid-top multigrid-top-right"
                classNameBottomLeftGrid="multigrid-left"
                classNameBottomRightGrid="multigrid-right"
                onScrollbarPresenceChange={(infos) => setScrollBarInfos(infos)}
              />
            </MultiGridContainer>
          )}
        </AutoSizer>
      </Content>
    </Container>
  );
};

const Container = styled.div<{ noPadding: boolean }>`
  flex: 1;
  height: 100%;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  padding: ${({ noPadding }) => (noPadding ? '0' : '10px 0')};
  margin: 0 auto;
  width: 100%;
`;

const ControlBar = styled.div<{ withPadding: boolean }>`
  max-width: 500px;
  display: flex;
  padding-bottom: 10px;
  gap: 5px;
  ${({ withPadding }) => (withPadding ? 'padding: 10px;' : '')}
`;

const Content = styled.div`
  flex: 1;
  height: 100%;
`;

const MultiGridContainer = styled.div<{ noBorderRadius: boolean }>`
  border: 1px solid ${RacemapColors.DarkGray};
  overflow: hidden;
  border-color: ${RacemapColors.LightGray};
  border-radius: ${({ noBorderRadius }) => (noBorderRadius ? '0' : '5px')};
  background-color: white;

  .multigrid-top {
    border-bottom: 1px solid ${RacemapColors.LightGray};
    will-change: unset !important;
  }

  .multigrid-left {
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-y: hidden;
  }

  .multigrid-left::-webkit-scrollbar {
    display: none;
  }

  .multigrid-top-right {
    overflow: hidden !important;
  }
`;
